import { ColumnDef } from '@tanstack/react-table'
import { ClientDataTable } from 'components/ClientDataTable/ClientDataTable'
import FbButton from 'components/FbUI/FbButton'
import { Tabs } from 'components/FbUI/Tabs'
import { useMemo, useState } from 'react'
import { CSVRow, InternalColumns } from '../importer-types'
import { useImporterViewModel, getColumnOption } from '../ImporterViewModel'

type FinishTab = 'matched' | 'unmatched'

export function ImportWizardFinishMatchingStep({
  show,
  data,
  selectedAction,
  onActionChange,
}: {
  show: boolean
  data: CSVRow[]
  selectedAction: 'create' | 'exclude'
  onActionChange: (action: 'create' | 'exclude') => void
}) {
  const viewModel = useImporterViewModel()
  const [tab, setTab] = useState<FinishTab>('matched')
  // Identify company_match columns from headers
  const companyMatchHeaders = useMemo(() => {
    return (
      viewModel.importData?.headers.filter(
        (h: { original: string; mapped: string }) =>
          getColumnOption(h.mapped)?.type === 'company_match'
      ) ?? []
    )
  }, [viewModel.importData?.headers])

  // Split data into matched and unmatched
  const { matchedData, unmatchedData } = useMemo(() => {
    const matched: CSVRow[] = []
    const unmatched: CSVRow[] = []
    data.forEach((row) => {
      const chain = row[InternalColumns.MATCHED_CHAIN]
      const door = row[InternalColumns.MATCHED_DOOR]
      if (chain || door) {
        matched.push(row)
      } else {
        unmatched.push(row)
      }
    })
    return { matchedData: matched, unmatchedData: unmatched }
  }, [data])

  const currentData = tab === 'matched' ? matchedData : unmatchedData

  const columnDefs: ColumnDef<CSVRow>[] = useMemo(() => {
    const columns: ColumnDef<CSVRow>[] = []

    // Input Data column
    columns.push({
      id: 'input_data',
      header: 'Input Data',
      accessorFn: (row) => {
        return companyMatchHeaders
          .map((h: { original: string }) => row[h.original] || '')
          .join(' ')
          .toLowerCase()
      },
      cell: ({ row }) => {
        const items: { label: string; value: string }[] = []

        companyMatchHeaders.forEach(
          (header: { original: string; mapped: string }) => {
            const value = row.original[header.original]
            if (value) {
              items.push({
                label: getColumnOption(header.mapped)?.name || header.mapped,
                value,
              })
            }
          }
        )

        return (
          <div className="space-y-0.5 text-sm">
            {items.map((item, idx) => (
              <div key={idx} className="text-gray-700">
                <span className="font-medium text-gray-500">{item.label}:</span>{' '}
                {item.value}
              </div>
            ))}
          </div>
        )
      },
    })

    // Matched Data column (sortable)
    columns.push({
      id: 'matched_data',
      header: 'Matched Data',
      accessorFn: (row) => {
        const chain = row[InternalColumns.MATCHED_CHAIN]
        const door = row[InternalColumns.MATCHED_DOOR]
        return (door?.name || chain?.name || '').toLowerCase()
      },
      cell: ({ row }) => {
        const chain = row.original[InternalColumns.MATCHED_CHAIN]
        const door = row.original[InternalColumns.MATCHED_DOOR]
        const isMatched = chain || door

        if (!isMatched) {
          return (
            <div className="text-gray-400 italic flex items-center gap-2">
              <span className="text-gray-400">○</span>
              <span>Unmatched</span>
            </div>
          )
        }

        const items: { label: string; value: string }[] = []

        if (door?.name || chain?.name) {
          items.push({ label: 'Name', value: door?.name || chain?.name || '-' })
        }
        if (door?.domain || chain?.domain) {
          items.push({
            label: 'Domain',
            value: door?.domain || chain?.domain || '-',
          })
        }
        if (door?.address) {
          items.push({ label: 'Address', value: door.address })
        }
        if (chain?.count || door?.chain_size) {
          items.push({
            label: 'Doors',
            value: String(chain?.count || door?.chain_size || '-'),
          })
        }

        return (
          <div className="space-y-0.5">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-green-600 font-bold">✓</span>
              <span className="font-semibold text-gray-900 text-sm">
                Matched
              </span>
            </div>
            <div className="space-y-0.5 text-sm">
              {items.map((item, idx) => (
                <div key={idx} className="text-gray-700">
                  <span className="font-medium text-gray-500">
                    {item.label}:
                  </span>{' '}
                  {item.value}
                </div>
              ))}
            </div>
          </div>
        )
      },
    })

    return columns
  }, [companyMatchHeaders])

  if (!show) {
    return null
  }

  return (
    <>
      <Tabs
        tabs={[
          {
            key: 'matched',
            title: `Matched (${matchedData.length})`,
          },
          {
            key: 'unmatched',
            title: `Unmatched (${unmatchedData.length})`,
          },
        ]}
        selectedTab={tab}
        setSelectedTab={setTab}
      />

      <ClientDataTable
        height={unmatchedData.length > 0 ? 520 : '100%'}
        data={currentData}
        columns={columnDefs}
        enableSorting
      />

      {unmatchedData.length > 0 && (
        <div className="flex items-center gap-3 mt-4">
          <span className="text-sm font-medium text-gray-700">
            Unmatched rows:
          </span>
          <div className="flex gap-1">
            <FbButton
              size="sm"
              variant={selectedAction === 'create' ? 'primary' : 'secondary'}
              onClick={() => onActionChange('create')}
            >
              Create Custom Companies
            </FbButton>
            <FbButton
              size="sm"
              variant={selectedAction === 'exclude' ? 'primary' : 'secondary'}
              onClick={() => onActionChange('exclude')}
            >
              Exclude from Import
            </FbButton>
          </div>
        </div>
      )}
    </>
  )
}
